<script lang="ts">
	import Layout from "../components/Layout.svelte";
	import { getTranslate } from "../utils/getTranslate.js";
	import translations from "./About.translations.json";
	import aboutHero from "../assets/images/about/about-hero.jpg?webp&h=1080&imagetools";
	import type { AboutProps } from "./AboutProps.js";
	import StaticPicture from "../../../shared/components/Picture/StaticPicture.svelte";
	import about1 from "../assets/images/schema.png?webp&h=542&imagetools";
	import about2 from "../assets/images/schema2.png?webp&h=600&imagetools";
	import adminInterface from "../assets/images/VertiNode_red-2.jpg?webp&h=441&imagetools";
	import userInterface from "../assets/images/userinterface.jpg?webp&h=927&imagetools";
	import displayTool from "../assets/images/Reference_MadalBal_15.jpg?webp&h=1209&imagetools";
	import { getVertiNodeLevelDetailPath } from "../../../../core/schema/paths/getVertiNodeLevelDetailPath.js";
	import { vertiNodeLevelsPathMap } from "../../../../core/schema/paths/vertiNodeLevelsPathMap.js";
	import Link from "../../../shared/components/Link.svelte";
	import Card from "../../../shared/components/Card.svelte";
	import { getGlobalSettings } from "../getGlobalSettings.js";
	import { contactPathMap } from "../../../../core/schema/paths/contactPathMap.js";
	import arrowRightLong from "../../../../core/assets/icons/arrow-right-long.svg?raw";

	type $$Props = AboutProps;

	const { vertiNodeLevels } = $$restProps as $$Props;

	const { TENANT_LOCALE } = getGlobalSettings();

	const translate = getTranslate(translations);
</script>

<Layout>
	<section class="container">
		<div class="flex aspect-[9/5] items-center overflow-hidden rounded-lg md:aspect-auto md:h-[26.25rem]">
			<StaticPicture
				image={aboutHero}
				alt={translate("h1")}
				width={1424}
				height={420}
				loading="eager"
				class="h-full w-full"
				imgClass="h-full w-full object-cover lg:object-[center] xl:object-[center]"
			/>
		</div>
	</section>
	<section class="container flex flex-col gap-8">
		<div class="mx-auto flex flex-col items-start lg:w-[50rem]">
			<h1 class="mx-0 lg:text-3xl">{translate("heading")}</h1>
			<p>{translate("p1")}</p>
			<h3 class="mx-0">{translate("h3-1")}</h3>
			<p>{translate("p2")}</p>
		</div>
		<StaticPicture
			image={about1}
			alt={translate("h3-1")}
			width={1392}
			height={542}
			loading="eager"
			imgClass="object-contain w-full rounded-lg"
			lightboxId="about"
		/>
	</section>
	<section class="container flex flex-col gap-8">
		<div class="mx-auto flex flex-col items-start lg:w-[50rem]">
			<h2 class="mx-0">{translate("h3-2")}</h2>
			<p>{translate("p3")}</p>
			<p>{translate("p4")}</p>
			<p class="text-2lg font-spaceGrotesk leading-snug">{translate("h3-3")}</p>
			<p class="mb-0">{translate("p5")}</p>
			<ul class="mb-4 list-inside list-disc">
				<li>{translate("li1")}</li>
				<li>{translate("li2")}</li>
				<li>{translate("li3")}</li>
				<li>{translate("li4")}</li>
			</ul>
			<p>{translate("p5-1")}</p>
			<Link
				asButton
				href="/{contactPathMap[TENANT_LOCALE]}#footerContact"
				icon={arrowRightLong}
				class="!inline-flex justify-between lg:w-auto lg:justify-normal"
			>
				{translate("contact")}
			</Link>
		</div>
		<StaticPicture
			image={about2}
			alt={translate("h3-3")}
			width={1024}
			height={600}
			loading="eager"
			imgClass="object-contain xl:min-w-[64rem] mx-auto rounded-lg"
			lightboxId="about"
		/>
	</section>
	<section class="container lg:w-[50rem]">
		<h2>{translate("h3-4")}</h2>
		<p>{translate("p6")}</p>
		<p>{translate("p7")}</p>
		<p>{translate("p8")}</p>
	</section>
	<section class="container">
		<h2 class="text-center">{translate("headingPackages")}</h2>
		<div class="flex flex-col gap-2 md:grid md:grid-cols-2 xl:grid-cols-4">
			{#each vertiNodeLevels as level}
				<Card
					title={level.name[TENANT_LOCALE]}
					description={level.firstParagraph}
					urlSlug={getVertiNodeLevelDetailPath(level)[TENANT_LOCALE]}
					squaredImage
					customButtonText={translate("showMore")}
				/>
			{/each}
		</div>
		<Link class="mx-auto mt-8 text-center" asButton variant="primary" href="/{vertiNodeLevelsPathMap[TENANT_LOCALE]}"
			>{translate("showAllFeatures")}</Link
		>
	</section>
	<section class="container flex flex-col gap-8">
		<div class="mx-auto flex flex-col lg:w-[50rem]">
			<h2 class="mx-0">{translate("h3-5")}</h2>
			<p>{translate("p9")}</p>
			<p class="text-2lg font-spaceGrotesk leading-snug">{translate("p10")}</p>
			<p>{translate("p11")}</p>
		</div>
		<StaticPicture
			image={adminInterface}
			alt="Placeholder"
			width={1392}
			height={441}
			loading="eager"
			imgClass="object-cover w-full max-h-[24rem] rounded-lg"
			lightboxId="about"
		/>
	</section>
	<section class="container flex flex-col gap-8">
		<div class="mx-auto flex flex-col items-start lg:w-[50rem]">
			<p class="text-2lg font-spaceGrotesk leading-snug">{translate("p12")}</p>
			<p>{translate("p13")}</p>
		</div>
		<StaticPicture
			image={userInterface}
			alt="Placeholder"
			width={1392}
			height={927}
			loading="eager"
			imgClass="object-cover w-full max-w-[64rem] mx-auto rounded-lg"
			lightboxId="about"
		/>
	</section>
	<section class="container flex flex-col gap-8">
		<div class="mx-auto flex flex-col items-start lg:w-[50rem]">
			<p class="text-2lg font-spaceGrotesk leading-snug">{translate("h3-6")}</p>
			<p>{translate("p15")}</p>
		</div>
		<StaticPicture
			image={displayTool}
			alt="Placeholder"
			width={470}
			height={1209}
			loading="eager"
			imgClass="object-cover w-full max-w-[64rem] mx-auto rounded-lg"
			lightboxId="about"
		/>
	</section>
	<section class="container lg:w-[50rem]">
		<div class="flex flex-col items-start">
			<p>{translate("p16")}</p>
			<Link
				asButton
				href="/{contactPathMap[TENANT_LOCALE]}#footerContact"
				icon={arrowRightLong}
				class="!inline-flex justify-between lg:w-auto lg:justify-normal"
			>
				{translate("contact")}
			</Link>
		</div>
	</section>
</Layout>
